// Decode the handful of entities that show up in stored post content.
const ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#x27;': "'",
  '&#39;': "'",
  '&apos;': "'",
  '&nbsp;': ' '
};

/**
 * Reduce Markdown to its readable text. Posts written in the board/card editor
 * are stored as Markdown (smart-card links `[text](url "smartCard-inline")`,
 * images, bullets, headings, emphasis, code ticks), and the raw syntax was
 * leaking into <title>, descriptions and JSON-LD.
 */
function markdownToPlainText(text: string): string {
  return (
    text
      // Fenced code blocks: keep the code, drop the fences.
      .replace(/```[^\n]*\n?/g, '')
      // Images: nothing readable in `![alt](url)` — the alt is usually a
      // filename (`image.png`), so drop the whole thing.
      .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
      // Links: keep the anchor text, drop the URL and any title.
      .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
      // Reference-style links `[text][ref]` and their definitions.
      .replace(/\[([^\]]*)\]\[[^\]]*\]/g, '$1')
      .replace(/^\s*\[[^\]]+\]:\s*\S+.*$/gm, '')
      // Autolinks `<https://…>`.
      .replace(/<(https?:\/\/[^>\s]+)>/g, '$1')
      // Headings, blockquotes, list bullets and task boxes at line start.
      .replace(/^\s{0,3}#{1,6}\s+/gm, '')
      .replace(/^\s{0,3}>\s?/gm, '')
      .replace(/^\s*(?:[-*+]|\d+[.)])\s+(?:\[[ xX]\]\s+)?/gm, '')
      // Horizontal rules and table separator rows.
      .replace(/^\s*(?:[-*_]\s*){3,}$/gm, '')
      .replace(/^\s*\|?(?:\s*:?-+:?\s*\|)+\s*$/gm, '')
      // Emphasis, strikethrough and inline code: keep the text. Underscore
      // emphasis only at word boundaries, so `List_of_records` stays intact
      // (CommonMark ignores intraword underscores too). No lookbehind: the
      // helper also runs in the browser, and older Safari fails to parse it.
      .replace(/(\*\*|__)(?=\S)([\s\S]*?\S)\1/g, '$2')
      .replace(/\*(?=\S)([^*\n]*?\S)\*/g, '$1')
      .replace(/(^|[^\w])_(?=\S)([^_\n]*?\S)_(?!\w)/g, '$1$2')
      .replace(/~~(?=\S)([\s\S]*?\S)~~/g, '$1')
      .replace(/`([^`\n]+)`/g, '$1')
  );
}

/**
 * Convert stored post `content` (sanitized HTML — see useContentUIRenderer — or
 * Markdown from the card editor) into clean plain text for
 * <title>/description/JSON-LD. Strips tags and Markdown syntax, decodes
 * entities, drops YouTube-transcript timestamp prefixes ("0:04 : "), collapses
 * whitespace. Server-safe: pure string ops, no DOM/DOMPurify (which only runs
 * client-side). Lives in @shared/all so the client (metadata/JSON-LD) and the
 * server (sitemap content-or-attachments gate) share one implementation.
 */
export function contentToPlainText(content: string | null | undefined): string {
  if (!content) return '';
  let text = content
    // Block/line tags become a space so adjacent words don't fuse together.
    .replace(/<\/(p|div|li|h[1-6]|blockquote|tr)>/gi, ' ')
    .replace(/<br\s*\/?>/gi, ' ')
    // Drop every remaining tag (anchors, spans, img, etc.).
    .replace(/<[^>]+>/g, '');
  text = markdownToPlainText(text);
  for (const [entity, char] of Object.entries(ENTITIES)) {
    text = text.split(entity).join(char);
  }
  text = text.replace(/&#(\d+);/g, (_, code) =>
    String.fromCharCode(Number(code))
  );
  // Transcript timestamp prefixes: "0:04 : ", "1:59:", "10:02 : "
  text = text.replace(/\b\d{1,2}:\d{2}\s*:\s*/g, '');
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * Cut `text` to at most `max` characters plus an ellipsis, at the last word
 * boundary before the limit so a title never ends mid-word. A single word longer
 * than the limit is cut hard — there is no boundary to prefer.
 */
export function truncateText(text: string, max: number): string {
  if (text.length <= max) return text;
  const hard = text.slice(0, max);
  // The hard cut already ends on a whole word when a space follows it.
  const endsOnWord = text.charAt(max) === ' ';
  const lastSpace = hard.lastIndexOf(' ');
  const cut = endsOnWord || lastSpace <= 0 ? hard : hard.slice(0, lastSpace);
  return cut.trimEnd() + '...';
}
